import { useLocation, useNavigate } from "react-router-dom";
import { BarChart2 } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

type LimitStatus = "at_limit" | "over_limit" | null;

interface LimitIssue {
    candidates: LimitStatus;
    jobs: LimitStatus;
}

export function getWorstSeverity(issue: LimitIssue | null): "none" | "at_limit" | "over_limit" {
    if (!issue) return "none";
    const statuses = [issue.candidates, issue.jobs];
    if (statuses.includes("over_limit")) return "over_limit";
    if (statuses.includes("at_limit")) return "at_limit";
    return "none";
}

export function buildLimitMessage(issue: LimitIssue, t: (key: string) => string): string {
    const parts: string[] = [];
    if (issue.candidates === "at_limit" || issue.candidates === "over_limit") parts.push(t("billing.limitBlocker.candidateRecords"));
    if (issue.jobs === "at_limit" || issue.jobs === "over_limit") parts.push(t("billing.limitBlocker.openJobOffers"));
    if (parts.length === 0) return t("billing.limitBlocker.aUsageLimit");
    if (parts.length === 1) return parts[0];
    return parts.join(" and ");
}

/**
 * Renders the full-screen blocking splash when the org is MORE than 10% over its limit.
 * Placed at the top of Layout (outside mobile/desktop conditional).
 */
export const LimitBlocker = () => {
    const { limitIssue, isSuperAdmin, isAdmin, isLimitInGracePeriod } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    if (isSuperAdmin) return null;
    if (location.pathname.startsWith("/admin")) return null;

    // Block when a limit issue exists and the grace period has expired
    if (!limitIssue || isLimitInGracePeriod) return null;

    const limitedThings = buildLimitMessage(limitIssue as LimitIssue, t);

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-md w-full mx-4 p-8 flex flex-col items-center text-center gap-6">
                <div className="bg-orange-100 dark:bg-orange-900/30 p-4 rounded-full">
                    <BarChart2 className="h-10 w-10 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="space-y-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {t("billing.limitBlocker.title")}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t("billing.limitBlocker.description", { limitedThings })}
                    </p>
                </div>
                <div className="flex flex-col gap-3 w-full">
                    {isAdmin && (
                        <Button
                            className="w-full bg-digger-blue hover:bg-digger-blue-light"
                            onClick={() => navigate("/admin?page=billing")}
                        >
                            {t("billing.viewBilling")}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => window.location.href = "mailto:billing@digger.be"}
                    >
                        {t("billing.contactDiggerTeam")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
